import React, { useEffect, useState } from 'react';
import { Button, TextField, Spinner, Text, Flex, DataList } from "@radix-ui/themes";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../api';
import { updateSchoolPaymentSettings } from '../../redux/slices/schoolAdminAccountSlice';

const PaymentSettingsForm = ({ setIsEditing }) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const schoolDetails = useSelector((state) => state.schoolAdmin.school);

    const formik = useFormik({
        initialValues: {
            charge_amount: schoolDetails?.payment_setting?.charge_amount ?? 40,
            school_percentage: schoolDetails?.payment_setting?.school_percentage ?? 0,
        },
        validationSchema: Yup.object({
            charge_amount: Yup.number()
                .min(40, 'Charge amount must be at least 40')
                .required('Charge amount is required'),
            school_percentage: Yup.number()
                .min(0, 'School percentage cannot be less than 0%')
                .max(60, 'School percentage cannot exceed 60%')
                .required('School percentage is required'),
        }),
        onSubmit: async (values) => {
            if (!schoolDetails?.id) {
                formik.setStatus('School ID is missing.');
                return;
            }
            try {
                formik.setStatus('');
                setLoading(true);
                const response = await api.put(`/payment-settings/${schoolDetails.id}`, {
                    charge_amount: values.charge_amount,
                    school_percentage: values.school_percentage,
                });
                const data = response.data;

                if (data.errors) {
                    formik.setErrors(data.errors);
                } else {
                    dispatch(updateSchoolPaymentSettings(data));
                    toast.success('Payment settings updated successfully', {
                        position: "bottom-right",
                    });
                    setIsEditing(false);
                }
            } catch (err) {
                const errorMessage = err.response?.data?.errors
                    ? 'Invalid input. Please check your values.'
                    : 'Update failed. Please try again.';
                formik.setStatus(errorMessage);
            } finally {
                setLoading(false);
            }
        },
    });

    const calculateDistribution = (chargeAmount, schoolPercentage) => {
        const teacherPercentage = 100 - 20 - schoolPercentage;
        const schoolAmount = (chargeAmount * (schoolPercentage / 100)).toFixed(2);
        const teacherAmount = (chargeAmount * (teacherPercentage / 100)).toFixed(2);
        return { teacherPercentage, schoolAmount, teacherAmount };
    };

    const { teacherPercentage, schoolAmount, teacherAmount } = calculateDistribution(
        formik.values.charge_amount,
        formik.values.school_percentage
    );

    const handlePercentageButtonClick = (percentage) => {
        formik.setFieldValue('school_percentage', percentage);
        formik.setFieldTouched('school_percentage', true);
    };

    return (
        <form onSubmit={formik.handleSubmit} disabled={loading}>
            <DataList.Root>
                <DataList.Item>
                    <DataList.Label minWidth="100px">Charge Amount</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="1">
                            <TextField.Root
                                size="3"
                                name="charge_amount"
                                type="number"
                                value={formik.values.charge_amount}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Charge Amount"
                                aria-label="Charge Amount"
                                disabled={loading}
                            />
                            {formik.touched.charge_amount && formik.errors.charge_amount && (
                                <Text color="red" size="2">{formik.errors.charge_amount}</Text>
                            )}
                        </Flex>
                    </DataList.Value>
                </DataList.Item>
                <DataList.Item>
                    <DataList.Label minWidth="100px">School Percentage</DataList.Label>
                    <DataList.Value>
                        <Flex direction="column" gap="2">
                            <Flex gap="2" wrap="wrap">
                                {[0, 15, 25, 50].map((percentage) => (
                                    <Button
                                        key={percentage}
                                        type="button"
                                        variant={formik.values.school_percentage === percentage ? 'solid' : 'outline'}
                                        onClick={() => handlePercentageButtonClick(percentage)}
                                        disabled={loading}
                                        aria-label={`Set school percentage to ${percentage}%`}
                                    >
                                        {percentage}%
                                    </Button>
                                ))}
                            </Flex>
                            <Flex direction="column" gap="1" mt="2">
                                <TextField.Root
                                    size="3"
                                    name="school_percentage"
                                    type="number"
                                    value={formik.values.school_percentage}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Custom Percentage (0-60)"
                                    aria-label="Custom School Percentage"
                                    disabled={loading}
                                />
                                {/* <Text size="2" mt="1">{formik.values.school_percentage}%</Text> */}
                                {formik.touched.school_percentage && formik.errors.school_percentage && (
                                    <Text color="red" size="2">{formik.errors.school_percentage}</Text>
                                )}
                            </Flex>
                        </Flex>
                    </DataList.Value>
                </DataList.Item>
            </DataList.Root>
            <div className="flex flex-col gap-1">
                <Text size="2" mt="2">
                    Revenue Distribution: Service Fee: 20%, School: {formik.values.school_percentage}% (GHC {schoolAmount}),
                    Teacher: {teacherPercentage}% (GHC {teacherAmount})
                </Text>
                {formik.status && (
                    <Text color="red" size="2" mt="2">{formik.status}</Text>
                )}
            </div>
            <Flex direction="row" justify="between" align="center" mt="4">
                <Button type="submit" disabled={loading}>
                    {loading ? <Spinner /> : 'Save'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsEditing(false)} disabled={loading}>
                    Cancel
                </Button>
            </Flex>
        </form>
    );
};

export default PaymentSettingsForm;